import { GridColDef } from "@mui/x-data-grid-pro";
import {
  GKClientDataGrid,
  GKTextField,
  StatusField,
  Typography,
} from "components";
import { FlattenedPlanRow, Portfolio } from "interfaces/OrderSimulation";
import { Controller, useFormContext } from "react-hook-form";
import { moneyCommaSeparator } from "utils";
import { extraColumnsField } from "utils/helpers";

const StockWiseRebalanceTable = () => {
  const form = useFormContext();

  const portfolios: Portfolio[] = form.getValues("generateTradeResponse") || [];

  const rows: FlattenedPlanRow[] = portfolios.flatMap((portfolio, pIndex) =>
    (portfolio.plan || []).map((plan, planIndex) => {
      const totalAmount = Number(plan.cmp || 0) * Number(plan.quantity || 0);

      form.setValue(
        `generateTradeResponse.${pIndex}.plan.${planIndex}.totalAmount`,
        totalAmount,
      );

      return {
        ...plan,
        portfolioId: portfolio.portfolioId,
        portfolioName: portfolio.portfolioName,
        portfolioCode: portfolio.portfolioCode,
        index: `${pIndex}-${planIndex}`,
        totalAmount,
      };
    }),
  );

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Portfolio Name", "portfolioName", 250, 1),
      renderCell: (params) => {
        const { portfolioName, portfolioCode } = params?.row;
        return (
          <Typography fontWeight={600}>
            {portfolioName}&nbsp;/&nbsp;{portfolioCode}
          </Typography>
        );
      },
    },
    {
      ...extraColumnsField("Model WT.", "versionWeightage", 120, 1),
      type: "number",
      valueFormatter: (params) => `${Number(params.value || 0)?.toFixed(2)}%`,
    },
    {
      ...extraColumnsField("Portfolio WT.", "portfolioWeightage", 120, 1),
      type: "number",
      valueFormatter: (value) => Number(value?.value || 0)?.toFixed(2),
    },
    {
      ...extraColumnsField("Deviation", "deviationPercent", 120, 1),
      type: "number",
      valueFormatter: (params) => `${Number(params.value || 0)?.toFixed(2)}%`,
    },
    {
      ...extraColumnsField("Trans. type", "transaction type", 120, 1),
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return <StatusField statusString={params.row["transaction type"]} />;
      },
    },
    {
      ...extraColumnsField("Ledger Balance (₹)", "ledgerBalance", 150, 1),
      type: "number",
      valueGetter: (value) => moneyCommaSeparator(value.value),
    },
    {
      ...extraColumnsField("Order Qty", "quantity", 120, 1),
      renderCell: (params) => {
        const rowIndex = rows.findIndex((row) => row.index === params.id);
        const rowData = rows[rowIndex];

        const portfolioIndex = portfolios.findIndex(
          (p) => p.portfolioId === rowData.portfolioId,
        );
        const planIndex = portfolios[portfolioIndex]?.plan?.findIndex(
          (pl) => pl.gridCode === rowData.gridCode,
        );

        const quantityPath =
          `generateTradeResponse.${portfolioIndex}.plan.${planIndex}.quantity` as const;
        const totalAmountPath =
          `generateTradeResponse.${portfolioIndex}.plan.${planIndex}.totalAmount` as const;

        return (
          <Controller
            name={quantityPath}
            control={form.control}
            defaultValue={rowData.quantity}
            render={({ field }) => (
              <GKTextField
                {...field}
                size="small"
                type="number"
                onChange={(e) => {
                  const newQty = e.target.value;
                  field.onChange(newQty === "" ? "" : Number(newQty));
                  form.setValue(totalAmountPath, +newQty * Number(rowData.cmp));
                }}
              />
            )}
          />
        );
      },
    },
    {
      ...extraColumnsField("Order Amount (₹)", "totalAmount", 150, 1),
      type: "number",
      valueFormatter: (params) =>
        moneyCommaSeparator(Number(params.value || 0)),
      renderCell: (params) => {
        const rowIndex = rows.findIndex((row) => row.index === params.id);
        const rowData = rows[rowIndex];

        const portfolioIndex = portfolios.findIndex(
          (p) => p.portfolioId === rowData.portfolioId,
        );

        const planIndex = portfolios[portfolioIndex]?.plan?.findIndex(
          (pl) => pl.gridCode === rowData.gridCode,
        );

        const totalAmountPath =
          `generateTradeResponse.${portfolioIndex}.plan.${planIndex}.totalAmount` as const;

        return (
          <Controller
            control={form.control}
            name={totalAmountPath}
            render={({ field }) => (
              <Typography fontSize={14}>
                {moneyCommaSeparator(field.value ?? 0)}
              </Typography>
            )}
          />
        );
      },
    },
    {
      ...extraColumnsField(
        "After Trade WT. (₹)",
        "afterTradeWeightage",
        150,
        1,
      ),
      type: "number",
      valueFormatter: (params) => `${Number(params.value || 0)?.toFixed(2)}%`,
    },
  ];

  return (
    <GKClientDataGrid
      name="stockWiseRebalanceOrders"
      pagination={false}
      pinnedColumns={{ left: ["portfolioName"] }}
      columns={columns}
      rows={rows}
      getRowId={(row) => row.index}
    />
  );
};

export default StockWiseRebalanceTable;
